// frontend/src/components/GPSDeviceManagement.js
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Navigation,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Link as LinkIcon,
  Unlink,
  Star,
  Activity,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import gpsDeviceService from '../services/gpsDeviceService';
import GPSDeviceDialog from './GPSDeviceDialog';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';

const GPSDeviceManagement = ({ currentEntity }) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();

  // Normalize entity identifier because the parent supplies either id or entityId.
  const entityId = currentEntity?.entityId || currentEntity?.id || null;

  // State management
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignment, setFilterAssignment] = useState('all');
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deletingDevice, setDeletingDevice] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [showConfigGuide, setShowConfigGuide] = useState(false);

  // Load data
  const loadDevices = useCallback(async () => {
    if (!entityId) {
      setDevices([]);
      setFilteredDevices([]);
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [devicesData, statsData] = await Promise.all([
        gpsDeviceService.getAllDevices(entityId, true),
        gpsDeviceService.getDeviceStats(entityId, true)
      ]);
      setDevices(devicesData);
      setFilteredDevices(devicesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading GPS devices:', error);
      toast.error(t('gpsDevices.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [entityId, t]);

  // Load vehicles for assignment dropdown
  const loadVehicles = useCallback(async () => {
    if (!entityId) {
      setVehicles([]);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/vehicles/${entityId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  }, [entityId]);

  useEffect(() => {
    if (entityId) {
      loadDevices();
      loadVehicles();
    } else {
      setLoading(false);
    }
  }, [entityId, loadDevices, loadVehicles]);

  // Filtering
  useEffect(() => {
    let filtered = [...devices];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(device =>
        device.device_name?.toLowerCase().includes(term) ||
        device.device_identifier?.toLowerCase().includes(term) ||
        device.device_type?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(device => device.status === filterStatus);
    }

    // Assignment filter
    if (filterAssignment === 'assigned') {
      filtered = filtered.filter(device => device.vehicle_id !== null);
    } else if (filterAssignment === 'unassigned') {
      filtered = filtered.filter(device => device.vehicle_id === null);
    }

    setFilteredDevices(filtered);
  }, [devices, searchTerm, filterStatus, filterAssignment]);

  // Get health status
  const getDeviceHealth = (device) => {
    return gpsDeviceService.getDeviceHealth(device);
  };

  // Get health icon
  const getHealthIcon = (health) => {
    switch (health) {
      case 'online':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'offline':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  // Format last contact time
  const formatLastContact = (lastContactAt) => {
    if (!lastContactAt) return t('gpsDevices.health.unknown');

    const now = new Date();
    const lastContact = new Date(lastContactAt);
    const diffMinutes = Math.floor((now - lastContact) / (1000 * 60));

    if (diffMinutes < 1) return t('common.justNow', { defaultValue: 'Just now' });
    if (diffMinutes < 60) return t('common.minutesAgo', { count: diffMinutes, defaultValue: `${diffMinutes} minutes ago` });

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return t('common.hoursAgo', { count: diffHours, defaultValue: `${diffHours} hours ago` });

    const diffDays = Math.floor(diffHours / 24);
    return t('common.daysAgo', { count: diffDays, defaultValue: `${diffDays} days ago` });
  };

  // Get vehicle name with unassigned indicator
  const getVehicleName = (vehicleId) => {
    if (!vehicleId) return t('common.unassigned', { defaultValue: 'Unassigned' });
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.vehicle_number : `Vehicle #${vehicleId}`;
  };

  // Handle device actions
  const handleAddDevice = () => {
    setSelectedDevice(null);
    setShowDeviceDialog(true);
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setShowDeviceDialog(true);
  };

  const handleDeleteDevice = (device) => {
    setDeviceToDelete(device);
  };

  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;
    try {
      setDeletingDevice(true);
      await gpsDeviceService.deleteDevice(entityId, deviceToDelete.id);
      toast.success(t('gpsDevices.deviceDeleted'));
      setDeviceToDelete(null);
      loadDevices();
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error(t('gpsDevices.errors.deleteFailed'));
    } finally {
      setDeletingDevice(false);
    }
  };

  const handleSetDefault = async (device) => {
    if (!device.vehicle_id) {
      toast.error(t('gpsDevices.errors.assignFailed'));
      return;
    }

    try {
      await gpsDeviceService.setAsDefault(entityId, device.id);
      toast.success(t('gpsDevices.deviceUpdated'));
      loadDevices();
    } catch (error) {
      console.error('Error setting default device:', error);
      toast.error(t('gpsDevices.errors.updateFailed'));
    }
  };

  const handleUnassign = async (device) => {
    try {
      await gpsDeviceService.unassignFromVehicle(entityId, device.id);
      toast.success(t('gpsDevices.deviceUnassigned'));
      loadDevices();
    } catch (error) {
      console.error('Error unassigning device:', error);
      toast.error(t('gpsDevices.errors.assignFailed'));
    }
  };

  const handleDeviceSaved = () => {
    setShowDeviceDialog(false);
    setSelectedDevice(null);
    loadDevices();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Header with Statistics */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Navigation className="w-8 h-8 text-blue-500" />
          <div>
            <h1 className={cn('text-2xl font-bold', text.primary)}>
              {t('gpsDevices.title')}
            </h1>
            <p className={cn('text-sm', text.secondary)}>
              {t('common.manage', { defaultValue: 'Manage' })} GPS {t('common.devices', { defaultValue: 'devices' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemedButton
            onClick={() => setShowConfigGuide(true)}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('gpsDevices.configGuide.button', { defaultValue: 'Configuration Guide' })}
          </ThemedButton>
          <ThemedButton onClick={handleAddDevice} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {t('gpsDevices.addDevice')}
          </ThemedButton>
        </div>
      </div>

      {/* Informational Banner: GPS Devices Are Optional */}
      <ThemedCard className={cn('p-4 border-l-4 border-blue-500', background.secondary)}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className={cn('text-sm font-semibold mb-1', text.primary)}>
              {t('gpsDevices.optionalInfo.title', { defaultValue: 'GPS Devices Are Optional' })}
            </h3>
            <p className={cn('text-sm', text.secondary)}>
              {t('gpsDevices.optionalInfo.description', {
                defaultValue: 'Hardware GPS devices are optional. Drivers can use their phone\'s built-in GPS for location tracking without registering any device. Only register hardware GPS devices if you want to use dedicated tracking hardware instead of driver phones.'
              })}
            </p>
          </div>
        </div>
      </ThemedCard>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.total')}</p>
                <p className={cn('text-2xl font-bold', text.primary)}>{stats.total || 0}</p>
              </div>
              <Navigation className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </ThemedCard>

          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.online')}</p>
                <p className="text-2xl font-bold text-green-500">{stats.online || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </ThemedCard>

          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.active')}</p>
                <p className="text-2xl font-bold text-blue-500">{stats.active || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </ThemedCard>

          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.assigned')}</p>
                <p className={cn('text-2xl font-bold', text.primary)}>{stats.assigned || 0}</p>
              </div>
              <LinkIcon className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </ThemedCard>

          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.inactive')}</p>
                <p className="text-2xl font-bold text-gray-500">{stats.inactive || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-gray-500 opacity-50" />
            </div>
          </ThemedCard>

          <ThemedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn('text-sm', text.secondary)}>{t('gpsDevices.statistics.maintenance')}</p>
                <p className="text-2xl font-bold text-orange-500">{stats.maintenance || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </ThemedCard>
        </div>
      )}

      {/* Filters */}
      <ThemedCard className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <ThemedInput
              type="text"
              placeholder={t('common.search', { defaultValue: 'Search' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={cn(
                'w-full pl-10 pr-4 py-2 rounded-lg border',
                background.input,
                text.primary,
                'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              )}
            >
              <option value="all">{t('common.allStatus', { defaultValue: 'All Status' })}</option>
              <option value="active">{t('gpsDevices.statusOptions.active')}</option>
              <option value="inactive">{t('gpsDevices.statusOptions.inactive')}</option>
              <option value="maintenance">{t('gpsDevices.statusOptions.maintenance')}</option>
            </select>
          </div>

          {/* Assignment Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterAssignment}
              onChange={(e) => setFilterAssignment(e.target.value)}
              className={cn(
                'w-full pl-10 pr-4 py-2 rounded-lg border',
                background.input,
                text.primary,
                'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              )}
            >
              <option value="all">{t('common.allAssignments', { defaultValue: 'All Assignments' })}</option>
              <option value="assigned">{t('common.assigned', { defaultValue: 'Assigned' })}</option>
              <option value="unassigned">{t('common.unassigned', { defaultValue: 'Unassigned' })}</option>
            </select>
          </div>
        </div>
      </ThemedCard>

      {/* Devices List */}
      {filteredDevices.length === 0 ? (
        <ThemedCard className="p-12">
          <div className="text-center">
            <Navigation className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className={cn('text-lg font-semibold mb-2', text.primary)}>
              {t('gpsDevices.noDevices')}
            </h3>
            <p className={cn('text-sm mb-4', text.secondary)}>
              {t('common.getStarted', { defaultValue: 'Get started by adding your first GPS device' })}
            </p>
            <ThemedButton onClick={handleAddDevice}>
              {t('gpsDevices.addDevice')}
            </ThemedButton>
          </div>
        </ThemedCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredDevices.map((device) => {
            const health = getDeviceHealth(device);
            return (
              <ThemedCard key={device.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      {getHealthIcon(health)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={cn('font-semibold truncate', text.primary)}>
                          {device.device_name}
                        </h3>
                        {device.is_default && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <p className={cn('text-xs truncate', text.secondary)}>
                        {device.device_identifier}
                      </p>
                      {device.device_type && (
                        <p className={cn('text-xs', text.secondary)}>
                          {t(`gpsDevices.typeOptions.${device.device_type}`, { defaultValue: device.device_type })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEditDevice(device)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('common.edit', { defaultValue: 'Edit' })}
                    >
                      <Edit className="w-4 h-4 text-blue-500" />
                    </button>
                    <button
                      onClick={() => handleDeleteDevice(device)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('common.delete', { defaultValue: 'Delete' })}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className={text.secondary}>{t('gpsDevices.status')}:</span>
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-medium',
                      device.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                      device.status === 'maintenance' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    )}>
                      {t(`gpsDevices.statusOptions.${device.status}`)}
                    </span>
                  </div>

                  {/* Health */}
                  <div className="flex items-center justify-between">
                    <span className={text.secondary}>{t('common.health', { defaultValue: 'Health' })}:</span>
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-medium',
                      health === 'online' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                      health === 'warning' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                    )}>
                      {t(`gpsDevices.health.${health}`)}
                    </span>
                  </div>

                  {/* Last Contact */}
                  <div className="flex items-center justify-between">
                    <span className={text.secondary}>{t('gpsDevices.lastContact')}:</span>
                    <span className={text.primary}>{formatLastContact(device.last_contact_at)}</span>
                  </div>

                  {/* Assigned Vehicle */}
                  <div className="flex items-center justify-between">
                    <span className={text.secondary}>{t('gpsDevices.assignedVehicle')}:</span>
                    {device.vehicle_id ? (
                      <span className={cn('font-medium', text.primary)}>
                        {getVehicleName(device.vehicle_id)}
                      </span>
                    ) : (
                      <span className={cn('italic', text.muted)}>
                        {getVehicleName(device.vehicle_id)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  {device.vehicle_id ? (
                    <>
                      <button
                        onClick={() => handleSetDefault(device)}
                        disabled={device.is_default}
                        className={cn(
                          'flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors',
                          device.is_default
                            ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 cursor-not-allowed'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800'
                        )}
                      >
                        <Star className="w-3 h-3" />
                        {t('gpsDevices.setAsDefault')}
                      </button>
                      <button
                        onClick={() => handleUnassign(device)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Unlink className="w-3 h-3" />
                        {t('gpsDevices.unassign')}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditDevice(device)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 transition-colors"
                    >
                      <LinkIcon className="w-3 h-3" />
                      {t('gpsDevices.assign')}
                    </button>
                  )}
                </div>
              </ThemedCard>
            );
          })}
        </div>
      )}

      {/* Device Dialog */}
      {showDeviceDialog && (
        <GPSDeviceDialog
          isOpen={showDeviceDialog}
          onClose={() => {
            setShowDeviceDialog(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
          entityId={entityId}
          vehicles={vehicles}
          onSaved={handleDeviceSaved}
        />
      )}

      {/* Configuration Guide Modal */}
      {showConfigGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className={cn('w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl', background.primary)}>
            <div className={cn('sticky top-0 z-10 flex items-center justify-between p-6 border-b', background.primary, border.default)}>
              <h2 className={cn('text-2xl font-bold', text.primary)}>
                {t('gpsDevices.configGuide.title', { defaultValue: 'GPS Device Configuration Guide' })}
              </h2>
              <button
                onClick={() => setShowConfigGuide(false)}
                className={cn('p-2 rounded-lg transition-colors', 'hover:bg-gray-100 dark:hover:bg-gray-700')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Why Device Shows Offline */}
              <div>
                <h3 className={cn('text-lg font-semibold mb-3', text.primary)}>
                  {t('gpsDevices.configGuide.whyOffline.title', { defaultValue: 'Why is my device showing offline?' })}
                </h3>
                <p className={cn('mb-2', text.secondary)}>
                  {t('gpsDevices.configGuide.whyOffline.description', {
                    defaultValue: 'A GPS device shows offline when it hasn\'t sent any location data to the server yet, or when it hasn\'t sent data for more than 15 minutes. The device needs to be configured to send HTTP POST requests to your server.'
                  })}
                </p>
              </div>

              {/* Configuration Steps */}
              <div>
                <h3 className={cn('text-lg font-semibold mb-3', text.primary)}>
                  {t('gpsDevices.configGuide.steps.title', { defaultValue: 'How to Configure Your GPS Device' })}
                </h3>
                <div className="space-y-4">
                  <div className={cn('p-4 rounded-lg', background.secondary)}>
                    <h4 className={cn('font-semibold mb-2', text.primary)}>
                      1. {t('gpsDevices.configGuide.steps.step1.title', { defaultValue: 'Find Your Device\'s API Key' })}
                    </h4>
                    <p className={cn('text-sm', text.secondary)}>
                      {t('gpsDevices.configGuide.steps.step1.description', {
                        defaultValue: 'Edit your GPS device in this interface to view and copy the API key. Save it securely as it\'s needed for authentication.'
                      })}
                    </p>
                  </div>

                  <div className={cn('p-4 rounded-lg', background.secondary)}>
                    <h4 className={cn('font-semibold mb-2', text.primary)}>
                      2. {t('gpsDevices.configGuide.steps.step2.title', { defaultValue: 'Configure Device Settings' })}
                    </h4>
                    <p className={cn('text-sm mb-2', text.secondary)}>
                      {t('gpsDevices.configGuide.steps.step2.description', {
                        defaultValue: 'Access your GPS device\'s configuration interface (usually via web or mobile app) and set:'
                      })}
                    </p>
                    <ul className={cn('text-sm space-y-1 ml-4 list-disc', text.secondary)}>
                      <li><strong>Protocol:</strong> HTTP/HTTPS</li>
                      <li><strong>Server URL:</strong> {window.location.origin}/api/location/device-update</li>
                      <li><strong>Method:</strong> POST</li>
                      <li><strong>Update Interval:</strong> 10 seconds (recommended for shuttles)</li>
                    </ul>
                    <div className={cn('mt-3 p-3 rounded text-xs', background.primary)}>
                      <p className={cn('font-semibold mb-1', text.primary)}>Update Interval Guidelines:</p>
                      <ul className={cn('space-y-1 ml-3 list-disc', text.secondary)}>
                        <li><strong>5-10 seconds:</strong> Active passenger trips (best for real-time ETAs)</li>
                        <li><strong>15 seconds:</strong> Light tracking, longer routes</li>
                        <li><strong>30+ seconds:</strong> Idle vehicles, parking monitoring only</li>
                      </ul>
                    </div>
                  </div>

                  <div className={cn('p-4 rounded-lg', background.secondary)}>
                    <h4 className={cn('font-semibold mb-2', text.primary)}>
                      3. {t('gpsDevices.configGuide.steps.step3.title', { defaultValue: 'Add Authentication Header' })}
                    </h4>
                    <p className={cn('text-sm mb-2', text.secondary)}>
                      {t('gpsDevices.configGuide.steps.step3.description', {
                        defaultValue: 'Add a custom HTTP header to authenticate requests:'
                      })}
                    </p>
                    <div className={cn('p-3 rounded font-mono text-xs', background.primary)}>
                      Authorization: Bearer [YOUR_API_KEY]
                    </div>
                  </div>

                  <div className={cn('p-4 rounded-lg', background.secondary)}>
                    <h4 className={cn('font-semibold mb-2', text.primary)}>
                      4. {t('gpsDevices.configGuide.steps.step4.title', { defaultValue: 'Verify Data Format' })}
                    </h4>
                    <p className={cn('text-sm mb-2', text.secondary)}>
                      {t('gpsDevices.configGuide.steps.step4.description', {
                        defaultValue: 'Ensure your device sends JSON data in this format:'
                      })}
                    </p>
                    <pre className={cn('p-3 rounded font-mono text-xs overflow-x-auto', background.primary)}>
{`{
  "device_identifier": "357561850441589",
  "latitude": 39.9042,
  "longitude": 116.4074,
  "timestamp": "2025-11-03T10:30:00Z",
  "speed": 45.5,
  "heading": 180,
  "accuracy": 5.0
}`}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Health Status Indicators */}
              <div>
                <h3 className={cn('text-lg font-semibold mb-3', text.primary)}>
                  {t('gpsDevices.configGuide.healthIndicators.title', { defaultValue: 'Device Health Status' })}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🟢</span>
                    <div>
                      <p className={cn('font-medium', text.primary)}>
                        {t('gpsDevices.health.online', { defaultValue: 'Online' })}
                      </p>
                      <p className={cn('text-sm', text.secondary)}>
                        {t('gpsDevices.configGuide.healthIndicators.online', { defaultValue: 'Last contact within 5 minutes' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🟡</span>
                    <div>
                      <p className={cn('font-medium', text.primary)}>
                        {t('gpsDevices.health.warning', { defaultValue: 'Warning' })}
                      </p>
                      <p className={cn('text-sm', text.secondary)}>
                        {t('gpsDevices.configGuide.healthIndicators.warning', { defaultValue: 'Last contact 5-15 minutes ago' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔴</span>
                    <div>
                      <p className={cn('font-medium', text.primary)}>
                        {t('gpsDevices.health.offline', { defaultValue: 'Offline' })}
                      </p>
                      <p className={cn('text-sm', text.secondary)}>
                        {t('gpsDevices.configGuide.healthIndicators.offline', { defaultValue: 'No contact for over 15 minutes or never contacted' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <div>
                <h3 className={cn('text-lg font-semibold mb-3', text.primary)}>
                  {t('gpsDevices.configGuide.troubleshooting.title', { defaultValue: 'Troubleshooting' })}
                </h3>
                <ul className={cn('space-y-2 text-sm', text.secondary)}>
                  <li>• Check that your device has cellular/internet connectivity</li>
                  <li>• Verify the API key is correct and copied completely</li>
                  <li>• Ensure the server URL is accessible from your device&apos;s network</li>
                  <li>• Check device logs for HTTP errors or authentication failures</li>
                  <li>• Confirm the device identifier (IMEI) matches exactly</li>
                </ul>
              </div>
            </div>

            <div className={cn('sticky bottom-0 p-6 border-t', background.primary, border.default)}>
              <ThemedButton onClick={() => setShowConfigGuide(false)} className="w-full">
                {t('common.close', { defaultValue: 'Close' })}
              </ThemedButton>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogShell
        isOpen={!!deviceToDelete}
        onClose={() => !deletingDevice && setDeviceToDelete(null)}
        onConfirm={confirmDeleteDevice}
        title={t('common.delete', { defaultValue: 'Delete' })}
        confirmLabel={t('common.delete', { defaultValue: 'Delete' })}
        confirmVariant="danger"
        confirmIcon={Trash2}
        isLoading={deletingDevice}
        isDisabled={deletingDevice}
        showIcon={true}
        iconVariant="danger"
        closeOnBackdropClick={!deletingDevice}
      >
        <p className={cn('text-sm', text.secondary)}>
          {t('gpsDevices.confirmDelete')}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default GPSDeviceManagement;
